/* eslint-disable max-lines */
import { hasModule, ia, mString } from 'lib/helpers/utility';

import NavigateToERACell from 'components/practice/BeyondBilling/PatientLedger/components/NavigateToERACell';
import ProceduresCell from 'components/practice/BeyondBilling/PatientLedger/components/ProceduresCell';
import isEstimated from 'components/practice/BeyondBilling/lib/isEstimated';
import { calculateProceduresTotal } from 'components/public/lib/utils';
import {
  APPOINTMENT_RESOURCE_OPTIONS,
  APPOINTMENT_TAG_OPTIONS,
  INVOICE_OPTIONS,
  PATIENT_OPTIONS,
  PROVIDER_OPTIONS,
  TAG_INVOICE_OPTIONS
} from 'components/shared/Filters/lib/filterOptions';
import TagsCell from 'components/shared/Tags/components/Cell/TagsCell';

import NavigatePopover from '../../components/InternalInvoicesNavigatePopover';
import { calculateAmountFromPercentage } from '../../components/Invoice/components/InvoiceFooter/InvoiceSummary/lib/helpers';
import NavigateToExternal from '../../components/Invoice/components/NavigateToExternal';
import { renderItemType } from '../renderType';

export const getColDefs = (formatDate, practiceModules) =>
  [
    {
      field: 'id',
      headerName: 'Invoice ID',
      maxWidth: 150,
      headerCheckboxSelection: true,
      checkboxSelection: true
    },
    {
      field: 'dateofservice',
      headerName: 'Date Of Service',
      valueFormatter: ({ value }) => formatDate(value),
      filter: 'agDateColumnFilter'
    },
    {
      field: 'patient_name',
      headerName: 'Patient'
    },
    {
      field: 'total_amount',
      headerName: 'Amount',
      valueFormatter: ({ value }) => mString(value || 0),
      aggFunc: 'sum'
    },
    {
      field: 'invoice_balance',
      headerName: 'Pt. Balance',
      valueFormatter: ({ value }) => mString(value || 0),
      aggFunc: 'sum'
    },
    hasModule(practiceModules, 'billing.claims') && {
      field: 'ins_balance',
      headerName: 'Ins. Balance',
      valueFormatter: ({ value }) => mString(value || 0),
      aggFunc: 'sum'
    },
    {
      headerName: 'CPT® Codes',
      field: 'procedures',
      cellRenderer: ProceduresCell,
      sortable: false,
      valueGetter: (params) => {
        const procedures = params?.data?.procedures || [];
        const finalProcedures = procedures
          .map((procedure) => procedure.procedure_code)
          .filter(Boolean);

        return finalProcedures;
      }
    },
    hasModule(practiceModules, 'billing.claims') && {
      field: 'era_eob',
      sortable: false,
      headerName: 'ERA/EOB',
      cellRenderer: NavigateToERACell
    },
    {
      field: 'external_invoice_ids',
      headerName: 'External Invoices',
      cellClass: 'no-border',
      cellRenderer: NavigateToExternal
    },
    {
      field: 'tagLinks',
      headerName: 'Tags',
      cellClass: 'no-border',
      minWidth: 300,
      valueFormatter: ({ value }) => {
        return ia(value)
          ? value
              .map((tagLink) => tagLink?.tag?.name)
              .filter(Boolean)
              .join(', ')
          : '';
      },
      cellRenderer: ({ value, data }) => (
        <TagsCell
          value={value}
          kind="invoice"
          data={data}
          query={{ keys: ['internal_invoices', 'invoice-tags'], object: 'invoices' }}
          refTable="invoice"
        />
      ),
      pinned: 'right',
      sortable: false
    },
    {
      field: 'actions',
      headerName: 'Actions',
      cellRenderer: NavigatePopover,
      maxWidth: 88,
      minWidth: 36,
      resizable: false,
      sortable: false,
      pinned: 'right'
    }
  ].filter(Boolean);

export const DEFAULT_COLUMN_DEFS = { enableRowGroup: true };

export const GRID_OPTIONS = {
  animateRows: false,
  rowGroupPanelShow: 'always',
  groupSelectsChildren: true,
  enableRangeSelection: true,
  suppressAggFuncInHeader: true
};

const ERA_OPTIONS = [
  { label: 'Pending', value: 'pending' },
  { label: 'Posted', value: 'posted' }
];

export const DEFAULT_FILTERS = (practiceModules) => {
  return {
    search: {
      type: 'search',
      fields: [
        { field: 'id', label: 'Invoice ID' },
        { field: 'patient_name', label: 'Patient' },
        { field: 'provider', label: 'Provider' }
      ]
    },
    date: {
      type: 'date-range',
      title: 'Date of Service',
      icon: 'calendar-mono'
    },
    invoice_id: INVOICE_OPTIONS,
    patient: PATIENT_OPTIONS,
    provider: { ...PROVIDER_OPTIONS, allowed_operators: ['is any of', 'is not', 'is'] },
    amount: {
      type: 'range',
      title: 'Amount',
      icon: 'dollar-circle-mono'
    },
    balance: {
      type: 'range',
      title: 'Balance',
      icon: 'dollar-square-mono'
    },
    ...(hasModule(practiceModules, 'billing.claims')
      ? {
          ins_balance: {
            type: 'range',
            title: 'Ins. Balance',
            icon: 'money-send-mono'
          }
        }
      : {}),
    ...(hasModule(practiceModules, 'core.calendar_scheduling.appointments')
      ? {
          appointment_resource: APPOINTMENT_RESOURCE_OPTIONS
        }
      : {}),
    ...(hasModule(practiceModules, 'core.calendar_scheduling.appointments')
      ? {
          appointmentTag: APPOINTMENT_TAG_OPTIONS
        }
      : {}),
    invoiceTag: TAG_INVOICE_OPTIONS,
    era: {
      queryFn: (_, data) => {
        const searchTerm = data.filters.searchTerm || '';
        return {
          options: ERA_OPTIONS.filter((status) =>
            status.label.toLowerCase().includes(searchTerm.toLowerCase())
          )
        };
      },
      queryKey: 'internal_invoices_statuses.options',
      title: 'Status',
      icon: 'status-mono',
      allowed_operators: ['is any of', 'is not', 'is']
    }
  };
};

export const getPracticeDefaultFilters = (
  practice,
  formatDate = () => '',
  practiceModules = []
) => {
  const newDefaultFilters = {
    ...DEFAULT_FILTERS(practiceModules),
    date: {
      values: {
        startDate: new Date(
          Math.min(new Date(practice.created_at).getTime(), new Date(0).getTime())
        ),
        endDate: new Date()
      },
      type: 'date-range',
      title: 'Date of Invoice',
      preview: (values) => `${formatDate(values.startDate)} - ${formatDate(values.endDate)}`
    }
  };

  return newDefaultFilters;
};

const getProcedureDetails = ({ procedures, claims, invoice }) => {
  return (
    ia(procedures) &&
    procedures.map((procedure) => {
      const {
        name,
        type,
        total_cost_cents,
        pt_balance,
        dateofservice,
        charge: procCharge,
        discount,
        state_tax_rate,
        local_tax_rate
      } = procedure || {};

      const procedureType = renderItemType(procedure);

      const finalProcedure = { ...procedure };

      finalProcedure.type = procedureType;

      if (['self_pay', 'cash'].includes(type)) {
        // finalProcedure.charge = pt_balance ?? total_cost_cents ?? 0;
        finalProcedure.charge = procCharge ?? pt_balance ?? total_cost_cents ?? 0;
      }

      const { charge, ins_adjustment, ins_payment, ins_balance } = calculateProceduresTotal([
        finalProcedure
      ]);

      const ptBalance = pt_balance ?? total_cost_cents ?? 0;
      const discountAmount = discount?.amount_cents || 0;

      const detailsObj = {
        ...procedure,
        ptBalance,
        invoiceId: procedure?.invoiceId || invoice?.id,
        details: name,
        type: procedureType,
        charge: charge ?? pt_balance ?? total_cost_cents ?? 0,
        ...(claims && {
          ins_payment: ins_payment || 0,
          ins_adjustment: ins_adjustment || 0
        }),
        dateofservice: dateofservice || invoice?.dateofservice,
        isEstimated: isEstimated({ procedure }),
        is_detailed: invoice?.is_detailed ?? true,
        invoiceStatus: procedure?.invoiceStatus || invoice?.status,
        state_tax: calculateAmountFromPercentage(state_tax_rate, ptBalance - discountAmount),
        local_tax: calculateAmountFromPercentage(local_tax_rate, ptBalance - discountAmount),
        ins_balance
      };

      return detailsObj;
    })
  );
};

export const modifyProducts = ({ products = [], isTotal = false }) => {
  if (!products?.length) return [];

  return (
    ia(products) &&
    products.map((product) => {
      const {
        name,
        total_amount_cents,
        sales_count,
        pt_balance,
        dateofservice,
        discount,
        state_tax_rate,
        local_tax_rate
      } = product || {};

      const finalSalesCount = sales_count ?? 1;
      const finalCharge = isTotal
        ? (total_amount_cents ?? 0)
        : (total_amount_cents ?? 0) / finalSalesCount;

      const discountAmount = discount?.amount_cents || 0;

      const detailsObj = {
        ...product,
        name,
        dateofservice,
        type: 'product',
        quantity: finalSalesCount,
        charge: finalCharge,
        pt_balance: pt_balance ?? total_amount_cents ?? 0,
        state_tax: calculateAmountFromPercentage(
          state_tax_rate * finalSalesCount,
          finalCharge - discountAmount
        ),
        local_tax: calculateAmountFromPercentage(
          local_tax_rate * finalSalesCount,
          finalCharge - discountAmount
        )
      };

      return detailsObj;
    })
  );
};

export const modifyPackages = ({ packages = [], isTotal = false }) => {
  if (!packages?.length) return [];

  return (
    ia(packages) &&
    packages.map((pack) => {
      const { name, total_amount_cents, sales_count, pt_balance, dateofservice, status } =
        pack || {};

      const finalSalesCount = sales_count || 1;
      const finalCharge = isTotal
        ? (total_amount_cents ?? 0)
        : (total_amount_cents ?? 0) / finalSalesCount;

      const detailsObj = {
        ...pack,
        name,
        type: 'package',
        quantity: finalSalesCount,
        charge: finalCharge,
        pt_balance: pt_balance ?? total_amount_cents ?? 0,
        dateofservice,
        status: status ? status : 'active'
      };

      return detailsObj;
    })
  );
};

export const getInvoiceDetails = (invoice, claims = null) => {
  const procedures = invoice?.procedures || [];
  const products = invoice?.products || [];
  const packages = invoice?.packages || [];

  const modifiedProducts = modifyProducts({ products }) || [];
  const modifiedPackages = modifyPackages({ packages }) || [];

  const services = [...procedures, ...modifiedProducts, ...modifiedPackages];

  const proceduresDetails = getProcedureDetails({
    procedures: services,
    claims,
    invoice
  });

  return proceduresDetails;
};
